import { defineActions } from '@noven-prime/lattice-core';
import { SUBMISSION_STATUSES, defaultNotificationSettings, } from '@denise-augusta/domain';
import { getSubmissionsStore } from './store.js';
/** Reject anything that could escape the inquiries prefix of the container. */
function assertSubmissionPath(path) {
    if (!path.startsWith('inquiries/') || path.includes('..')) {
        throw new Error(`Not a submission path: ${path}`);
    }
}
/**
 * Form Submissions capability actions — admin only. The public write path
 * lives in the /api/contact function; everything here is the portal inbox.
 */
export const formSubmissionsActions = defineActions({
    listSubmissions: {
        id: 'listSubmissions',
        name: 'List Submissions',
        description: 'List contact-form inquiries, newest first',
        params: { type: 'object', properties: {} },
        result: { type: 'array' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (_params, _context) => {
            const submissions = await getSubmissionsStore().list();
            return submissions.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
        },
    },
    setSubmissionStatus: {
        id: 'setSubmissionStatus',
        name: 'Set Submission Status',
        description: 'Mark an inquiry new, read or archived',
        params: {
            type: 'object',
            properties: {
                path: { type: 'string', description: 'Blob path of the submission' },
                status: { type: 'string', description: 'new | read | archived' },
            },
            required: ['path', 'status'],
        },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (params, _context) => {
            assertSubmissionPath(params.path);
            if (!SUBMISSION_STATUSES.includes(params.status)) {
                throw new Error(`Unknown submission status: ${params.status}`);
            }
            await getSubmissionsStore().setStatus(params.path, params.status);
            return { path: params.path, status: params.status };
        },
    },
    deleteSubmission: {
        id: 'deleteSubmission',
        name: 'Delete Submission',
        description: 'Permanently delete an inquiry record',
        params: {
            type: 'object',
            properties: {
                path: { type: 'string', description: 'Blob path of the submission' },
            },
            required: ['path'],
        },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (params, _context) => {
            assertSubmissionPath(params.path);
            await getSubmissionsStore().remove(params.path);
            return { path: params.path };
        },
    },
    getNotificationSettings: {
        id: 'getNotificationSettings',
        name: 'Get Notification Settings',
        description: 'Get who is emailed when an inquiry arrives',
        params: { type: 'object', properties: {} },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (_params, _context) => {
            const settings = await getSubmissionsStore().loadNotificationSettings();
            return settings ?? defaultNotificationSettings;
        },
    },
    updateNotificationSettings: {
        id: 'updateNotificationSettings',
        name: 'Update Notification Settings',
        description: 'Change who is emailed when an inquiry arrives',
        params: {
            type: 'object',
            properties: {
                settings: { type: 'object', description: 'The full NotificationSettings document' },
            },
            required: ['settings'],
        },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (params, _context) => {
            const recipients = (params.settings.recipients ?? [])
                .map((address) => address.trim())
                .filter(Boolean);
            for (const address of recipients) {
                if (!/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(address)) {
                    throw new Error(`Not a valid email address: ${address}`);
                }
            }
            const next = {
                ...params.settings,
                recipients,
                updatedAt: new Date().toISOString(),
            };
            await getSubmissionsStore().saveNotificationSettings(next);
            return next;
        },
    },
});
//# sourceMappingURL=actions.js.map